import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject, Signal } from '@angular/core';
import { Router } from '@angular/router';
import { createCustomStateSetOnUrlHandler, createStateResetHandler } from '@appState/helpers/state-reset-handler';
import { addEntity, getAll, StateDictionary } from '@appState/helpers/store-base';
import { AlertType, AlertTypeCreateRequest } from '@models/index';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { AlertTypesService } from './alert-types.service';

interface AlertTypesState {
  alertTypes: AlertType[];
  selectedAlertTypeId: string | null;
  isLoading: boolean;
  isAlertTypeCreateLoading: boolean;
}

const initialState: AlertTypesState = {
  alertTypes: [],
  selectedAlertTypeId: null,
  isLoading: false,
  isAlertTypeCreateLoading: false,
};

const alertTypesDictionary: StateDictionary<AlertType> = {
  entityList: 'alertTypes',
  listLoading: 'isLoading',
  createLoading: 'isAlertTypeCreateLoading',
  selectedEntityLoading: 'isLoading',
  selectedEntity: '',
  entityId: 'id',
  sortFn: (a: AlertType, b: AlertType) => a.name.localeCompare(b.name),
};

export const AlertTypesStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withDevtools('alert-types-store'),
  withComputed(state => ({
    selectedAlertType: computed(() => {
      const items = state.alertTypes();
      const selectedId = state.selectedAlertTypeId();
      return selectedId ? items.find((item: AlertType) => item.id === selectedId) : null;
    }),
  })),
  withMethods((store, alertTypesService = inject(AlertTypesService), router = inject(Router)) => {
    createStateResetHandler<AlertTypesState>('/monitoring/alert-types')(store, initialState);
    createCustomStateSetOnUrlHandler<AlertTypesState>('/monitoring/alert-types', {
      selectedAlertTypeId: null,
    })(store);

    const repositoryMethods = {
      getAll: getAll<AlertType>({
        store,
        service: alertTypesService,
        dictionary: alertTypesDictionary,
      }),

      add: addEntity<AlertType, AlertTypeCreateRequest>({
        store,
        service: alertTypesService,
        dictionary: alertTypesDictionary,
        onSuccessCallback: (created: AlertType) => {
          patchState(store, { selectedAlertTypeId: created.id });
          router.navigate(['/monitoring', 'alert-types', created.id, 'details']);
        },
      }),
    };

    const navigationActions = {
      selectAlertType: (id: string): void => {
        if (!store.alertTypes().length && !store.isLoading()) repositoryMethods.getAll();
        patchState(store, { selectedAlertTypeId: id });
      },

      goToActions: async (id: string): Promise<boolean> => {
        const result = await router.navigate(['/monitoring', 'alert-types', id]);
        if (!result) return false;
        patchState(store, { selectedAlertTypeId: id });
        return result;
      },
    };

    return { ...repositoryMethods, ...navigationActions };
  }),
);

export interface AlertTypesStoreType {
  readonly alertTypes: Signal<AlertType[]>;
  readonly isLoading: Signal<boolean>;
  readonly isAlertTypeCreateLoading: Signal<boolean>;
  readonly selectedAlertType: Signal<AlertType | null | undefined>;
  readonly selectedAlertTypeId: Signal<string | null>;

  readonly getAll: () => void;
  readonly add: (dto: AlertTypeCreateRequest) => void;
  readonly selectAlertType: (id: string) => void;
  readonly goToActions: (id: string) => Promise<boolean>;
}
